import { ReactNode, createContext, useContext, useEffect, useState } from 'react'

import * as api from 'api/api'

export type Density = 'comfortable' | 'compact'

const STORAGE_KEY = 'jopath.density'

const hasStoredChoice = (): boolean => {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY))
  } catch {
    return false
  }
}

const readStored = (): Density => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'compact' ? 'compact' : 'comfortable'
  } catch {
    return 'comfortable'
  }
}

const DensityContext = createContext<{ density: Density; setDensity: (value: Density) => void }>({
  density: 'comfortable',
  setDensity: () => undefined,
})

/** Applies the user's row-density preference as a `data-density` attribute on <html>. */
export const DensityProvider = ({ children }: { children: ReactNode }) => {
  const [density, setDensityState] = useState<Density>(readStored)

  useEffect(() => {
    document.documentElement.dataset.density = density
  }, [density])

  useEffect(() => {
    // Settings → Appearance "default for everyone": only applies when the user has never picked their own
    // density here — an explicit choice always wins. Not persisted, so it keeps following the live org default.
    if (hasStoredChoice()) return
    api
      .fetchAppSettings()
      .then((settings) => {
        if (!hasStoredChoice() && (settings.defaultDensity === 'comfortable' || settings.defaultDensity === 'compact')) {
          setDensityState(settings.defaultDensity)
        }
      })
      .catch(() => undefined)
  }, [])

  const setDensity = (value: Density) => {
    setDensityState(value)
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      // per-browser preference only; safe to ignore storage failures (private mode, quota, etc.)
    }
  }

  return <DensityContext.Provider value={{ density, setDensity }}>{children}</DensityContext.Provider>
}

export const useDensity = () => useContext(DensityContext)
